/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Smarts Conversation UI
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

/**
 * This is a temporary class that is used to generate dashboard spec and will be replaced when Dashboard's API for generating
 * spec is ready.
 * @param id
 * @param dashboardSpecVersion
 * @returns {{build: (function(*, *=): {id: *, boardSpec: {name: string, theme: string, version: number, layout: {id: string, items: Array, style: {height: string}, type: string, pageSize: {width: number, height: number}, layoutPositioning: string}, dataSources: {version: string, sources: *[]}, widgets, eventGroups: Array}, isAuthoringMode: boolean}), buildTabContents: (function(*, *=): *)}}
 */
function dashboardSpecBuilder(id, dashboardSpecVersion) {
	const tabItems = [] //contains containers list

	let layout = {
		id: `${id}_layout`,
		items: tabItems,
		style: {
			height: '100%'
		},
		type: 'tab',
		pageSize: {
			width: 1280,
			height: 720
		},
		layoutPositioning: 'relative'
	}

	let dataSources = {
		version: '1.0',
		sources: [{
			shaping: {
				'embeddedModuleUpToDate': true
			}
		}]
	}

	let widgets = {}
	let eventGroups = []

	let boardSpec = {
		name: 'New dashboard',
		theme: 'defaultTheme',
		version: dashboardSpecVersion,
		layout: layout,
		dataSources: dataSources,
		widgets: widgets,
		eventGroups: eventGroups
	}

	let dashboardSpec = {
		id: id,
		boardSpec: boardSpec,
		isAuthoringMode: true
	}

	function createWidgetGenericPageItem( widgetId, style, label){
		return {
			id: widgetId,
			style: style,
			type: 'widget',
			name: label
		}
	}

	function addEventGroup(tabId, widgetIds){
		const eventGroupItem = {
			id: `${tabId}:1`,
			widgetIds
		}
		eventGroups.push( eventGroupItem )
	}


	function setSourceInfo({ modelRefId:id, sourceType:type, sourceId:assetId }){
		Object.assign(dataSources.sources[0], { assetId, type,  id })
	}

	function createTabWithContent(label, tabIndex, chartInfoList){
		let genericPageItems = [{
			id: `${id}_templateIndicator_${tabIndex}`,
			style: {
				top: '0%',
				left: '0%',
				right: '0%',
				bottom: '0%'
			},
			type: 'templateIndicator'
		}]

		let genericPage =  {
			id: `${id}_genericPage_${tabIndex}`,
			css: 'templateBox aspectRatio_default',
			items: genericPageItems,
			type: 'genericPage',
			layoutPositioning: 'relative'
		}

		const containerSpec =  {
			id: `${id}_container_${tabIndex}`,
			items: [ genericPage ],
			type: 'container',
			title: {
				translationTable: {
					Default: label || `Tab ${tabIndex}`
				}
			},
			templateName: 'Template9'
		}

		// Add livewidgets - both the layout and the spec
		let widgetIds = []
		chartInfoList.forEach( (chartInfo, index) => {
			const { chart, spec, style } = chartInfo
			const widgetId = `widget_${tabIndex}${index}` + Date.now()
			spec.id = widgetId
			genericPageItems.push(createWidgetGenericPageItem(widgetId, style, chart.label))
			widgets[widgetId] = spec
			widgetIds.push(widgetId)
		})

		return { containerSpec : containerSpec, widgetIds }

	}

	/**
	 * Add eventgroup and widgets to tab
	 * @param label
	 * @param tabIndex
	 * @param chartInfoList
	 */
	function addTab( label, tabIndex, chartInfoList ){
		const tab = createTabWithContent( label, tabIndex, chartInfoList)
		tabItems.push( tab.containerSpec )
		addEventGroup(tab.containerSpec.id, tab.widgetIds)
	}

	/**
	 * Build tab contents to be consumed by DashboardAPI.CanvasAPI.addContent()
	 * @param contents
	 * @param sourceInfo
	 * @returns {*}
	 */
	function buildTabContents( contents, sourceInfo ) {
		return contents.map(({ chartInfoList, tabLabel }, tabIndex) => {
			widgets = {}
			const tab = createTabWithContent(tabLabel, tabIndex, chartInfoList)
			setSourceInfo(sourceInfo)
			return { widgets, layout : tab.containerSpec, dataSources }
		})
	}

	function build( contents, sourceInfo ){
		contents.forEach( ({ tabLabel, chartInfoList }, index ) => {
			addTab(tabLabel, index, chartInfoList)
		})

		setSourceInfo(sourceInfo)
		return dashboardSpec
	}

	return {
		build,
		buildTabContents
	}
}

const NewDashboardSpec = {
	isAuthoringMode: true,
	boardSpec: {
		name: 'New dashboard',
		layout: {
			id: 'page0_' + Date.now(),
			items: [ {
				id: `container_1`,
				items: [],
				type: 'container',
				title: {
					translationTable: {
						Default: 'Tab 1'
					}
				},
				templateName: 'Template9'
			}],
			style: {
				height: '100%'
			},
			type: 'tab',
			showGrid: true,
			snapGrid: true,
			snapObjects: true,
			pageSize: {
				width: 1280,
				height: 720
			},
			layoutPositioning: 'relative'
		},
		pageContext: [],
		dataSources: {
			version: '1.0',
			sources: []
		},
		widgets: {}
	}
}

export default {
	build(id, contents, sourceInfo, dashboardSpecVersion) {
		return dashboardSpecBuilder(id, dashboardSpecVersion).build(contents, sourceInfo)
	},


	buildTabContents(id, contents, sourceInfo) {
		return dashboardSpecBuilder(id).buildTabContents(contents, sourceInfo)
	},

	getNewDashboardSpec(){
		NewDashboardSpec.id ='dashboard_' + Date.now()
		return NewDashboardSpec
	},

	/**
	 * Add id to widgets, remove model, add modelRef and (if available) autoBinning
	 * @param id
	 * @param widgetId
	 * @param chartRecommendation
	 * @param spec
	 * @returns updated spec
	 * @private
	 */
	fixupLiveWidgetSpec(spec, modelRefId, chartRecommendation, visFilterConverter) {
		spec = this._addProperties(spec)

		if (spec.data) {
			const dataViews = spec.data.dataViews

			dataViews.forEach(dataView => {
				//Fix up model info
				dataView.modelRef = modelRefId
				delete dataView.model
			})
		}

		visFilterConverter.convertVisFiltersToLiveWidgetSpec(spec, chartRecommendation)

		return spec
	},

	_addProperties(spec) {
		spec.showTitle = !(spec.visId === 'summary')
		spec.borderColor = 'color2'
		spec.type = spec.type ? spec.type : 'live'

		spec.properties = [{
			id: 'widget.legend.position',
			value: 'bottom'
		}]

		if( spec.visId === 'com.ibm.vis.rave2marimekko'){
			spec.properties.push({
				id: 'stacked.percent',
				value: false
			})
		}

		return spec
	}
}
